import { reachesJobs } from '@k-management/shared';
import { Navigate, Route, Routes } from 'react-router';
import { useAuth } from './auth/AuthProvider.js';
import { AppLayout } from './components/AppLayout.js';
import { GestifyBrowserProvider } from './lib/gestify-browser.js';
import { AccessRefusedPage } from './pages/AccessRefusedPage.js';
import { AuditPage } from './pages/admin/AuditPage.js';
import { BackupsPage } from './pages/admin/BackupsPage.js';
import { GestifyPage } from './pages/admin/GestifyPage.js';
import { SharePointPage } from './pages/admin/SharePointPage.js';
import { ProfilesPage } from './pages/admin/ProfilesPage.js';
import { UsersPage } from './pages/admin/UsersPage.js';
import { HomePage } from './pages/HomePage.js';
import { ProjectAreasPage } from './pages/jobs/ProjectAreasPage.js';
import { JobPage } from './pages/jobs/JobPage.js';
import { JobsListPage } from './pages/jobs/JobsListPage.js';
import { ResponsibilitiesPage } from './pages/organization/ResponsibilitiesPage.js';
import { RolesPage } from './pages/organization/RolesPage.js';
import { RulesPage } from './pages/organization/RulesPage.js';
import { QuoteDocumentPage } from './pages/quotes/documents/QuoteDocumentPage.js';
import { NewQuotePage } from './pages/quotes/NewQuotePage.js';
import { QuotePage } from './pages/quotes/QuotePage.js';
import { QuotesListPage } from './pages/quotes/QuotesListPage.js';
import { ImportPage } from './pages/registry/ImportPage.js';
import { NewSubjectPage } from './pages/registry/NewSubjectPage.js';
import { RegistryListPage } from './pages/registry/RegistryListPage.js';
import { SubjectPage } from './pages/registry/SubjectPage.js';
import { UnlockRequestsPage } from './pages/registry/UnlockRequestsPage.js';
import { SignInPage } from './pages/SignInPage.js';
import { TablesPage } from './pages/tables/TablesPage.js';
import { AdvancesPage } from './pages/treasury/AdvancesPage.js';
import { SelfBillingPage } from './pages/treasury/SelfBillingPage.js';
import { TransfersPage } from './pages/treasury/TransfersPage.js';
import { InstalmentPlanPage, InstalmentPlansPage } from './pages/treasury/InstalmentPlansPage.js';
import { BankMovementsPage } from './pages/treasury/BankMovementsPage.js';
import { BanksPage } from './pages/treasury/BanksPage.js';
import { ReclassPlansPage } from './pages/controlling/ReclassPlansPage.js';
import { DashboardPage } from './pages/reports/DashboardPage.js';
import { EmployeePage } from './pages/people/EmployeePage.js';
import { EmployeesPage } from './pages/people/EmployeesPage.js';
import { MonthSalariesPage } from './pages/people/MonthSalariesPage.js';
import { PayrollCostPage } from './pages/people/PayrollCostPage.js';
import { EInvoicePage } from './pages/treasury/EInvoicePage.js';
import { ReconcileWindowPage } from './pages/treasury/ReconcileWindowPage.js';
import { InvoicePage } from './pages/treasury/InvoicePage.js';
import { ManualInvoicePage } from './pages/treasury/ManualInvoicePage.js';
import { RegistryProposalsPage } from './pages/treasury/RegistryProposalsPage.js';
import { LedgersPage } from './pages/treasury/LedgersPage.js';
import { CardsPage } from './pages/treasury/CardsPage.js';
import { LoanPage } from './pages/treasury/LoanPage.js';
import { LoansPage } from './pages/treasury/LoansPage.js';
import { OpeningPage } from './pages/treasury/OpeningPage.js';
import { StatementPage, StatementsPage } from './pages/treasury/StatementsPage.js';
import { StatementPrintPage } from './pages/treasury/StatementPrintPage.js';
import { InvoicesPage } from './pages/treasury/InvoicesPage.js';
import { SchedulePage } from './pages/treasury/SchedulePage.js';
import { WorklistPage } from './pages/treasury/WorklistPage.js';
import { YearClosingPage } from './pages/treasury/YearClosingPage.js';
import { F24DeliveryPage, F24Page } from './pages/treasury/F24Page.js';

export function App() {
  const { state } = useAuth();

  switch (state.status) {
    case 'loading':
      return <p className="centered muted">Caricamento…</p>;
    case 'failed':
      return (
        <main className="centered">
          <h1>K-Management</h1>
          <p className="error">Impossibile avviare l’applicazione: {state.message}</p>
          <button type="button" onClick={() => window.location.reload()}>
            Riprova
          </button>
        </main>
      );
    case 'signed-out':
      return <SignInPage />;
    case 'refused':
      return <AccessRefusedPage refusal={state} />;
    case 'signed-in': {
      const { user } = state;
      // The Gestify session of the browser belongs to this user: a new sign-in starts without it.
      return (
        <GestifyBrowserProvider key={user.id}>
          <Routes>
            {/* The documents print without the frame of the app. */}
            {user.permissions.includes('preventivatore.read') && (
              <Route path="preventivatore/:id/documento/:kind" element={<QuoteDocumentPage />} />
            )}
            {user.permissions.includes('tesoreria.read') && (
              <Route path="tesoreria/fatture/:id/fattura-elettronica" element={<EInvoicePage />} />
            )}
            {user.permissions.includes('tesoreria.read') && (
              <Route
                path="tesoreria/schede-contabili/:id/stampa"
                element={<StatementPrintPage />}
              />
            )}
            {user.permissions.includes('tesoreria.read') && (
              <Route
                path="tesoreria/banche/:code/movimenti/:movementId/riconcilia"
                element={<ReconcileWindowPage />}
              />
            )}
            <Route element={<AppLayout />}>
              <Route index element={<HomePage />} />
              {user.permissions.includes('tabelle.read') && (
                <>
                  <Route path="tabelle" element={<TablesPage />} />
                  <Route path="tabelle/:key" element={<TablesPage />} />
                </>
              )}
              {user.permissions.includes('anagrafica.read') && (
                <>
                  <Route path="anagrafica" element={<RegistryListPage />} />
                  {user.permissions.includes('anagrafica.write') && (
                    <Route path="anagrafica/nuovo" element={<NewSubjectPage />} />
                  )}
                  {user.permissions.includes('anagrafica.admin') && (
                    <>
                      <Route path="anagrafica/richieste" element={<UnlockRequestsPage />} />
                      <Route path="anagrafica/importa" element={<ImportPage />} />
                    </>
                  )}
                  <Route path="anagrafica/:id" element={<SubjectPage />} />
                </>
              )}
              {user.permissions.includes('preventivatore.read') && (
                <>
                  <Route path="preventivatore" element={<QuotesListPage phase="preventivo" />} />
                  <Route path="offerte" element={<QuotesListPage phase="offerta" />} />
                  {user.permissions.includes('preventivatore.write') && (
                    <Route path="preventivatore/nuovo" element={<NewQuotePage />} />
                  )}
                  <Route path="preventivatore/:id" element={<QuotePage />} />
                  {/* An offer lives under «Offerte»: same page, its own place in the menu. */}
                  <Route path="offerte/:id" element={<QuotePage />} />
                </>
              )}
              {user.permissions.includes('commesse.read') && (
                <>
                  <Route
                    path="commesse"
                    element={<JobsListPage key="commessa" kind="commessa" />}
                  />
                  <Route path="commesse/:id" element={<JobPage key="commessa" kind="commessa" />} />
                </>
              )}
              {/* The progetti (2.41): also for who has an area of them or is their PM (ADR-025). */}
              {reachesJobs(user, 'read') && (
                <>
                  <Route
                    path="progetti"
                    element={<JobsListPage key="progetto" kind="progetto" />}
                  />
                  {user.permissions.includes('commesse.admin') && (
                    <Route path="progetti/aree" element={<ProjectAreasPage />} />
                  )}
                  <Route path="progetti/:id" element={<JobPage key="progetto" kind="progetto" />} />
                </>
              )}
              {user.permissions.includes('tesoreria.read') && (
                <>
                  <Route
                    path="tesoreria"
                    element={<Navigate to="/tesoreria/da-sistemare" replace />}
                  />
                  <Route path="tesoreria/da-sistemare" element={<WorklistPage />} />
                  <Route path="tesoreria/chiusura-anno" element={<YearClosingPage />} />
                  <Route
                    path="tesoreria/fatture"
                    element={<Navigate to="/tesoreria/fatture-attive" replace />}
                  />
                  <Route
                    path="tesoreria/fatture-attive"
                    element={<InvoicesPage key="emessa" direction="emessa" />}
                  />
                  <Route
                    path="tesoreria/fatture-passive"
                    element={<InvoicesPage key="ricevuta" direction="ricevuta" />}
                  />
                  <Route path="tesoreria/fatture/:id" element={<InvoicePage />} />
                  {user.permissions.includes('tesoreria.write') && (
                    <>
                      <Route
                        path="tesoreria/fatture-passive/nuovo"
                        element={<ManualInvoicePage key="nuovo" />}
                      />
                      <Route
                        path="tesoreria/fatture/:id/modifica"
                        element={<ManualInvoicePage />}
                      />
                      <Route
                        path="tesoreria/anagrafica-dalle-fatture"
                        element={<RegistryProposalsPage />}
                      />
                    </>
                  )}
                  <Route path="tesoreria/mastrini" element={<LedgersPage />} />
                  <Route path="tesoreria/situazione-iniziale" element={<OpeningPage />} />
                  <Route path="tesoreria/schede-contabili" element={<StatementsPage />} />
                  <Route path="tesoreria/schede-contabili/:id" element={<StatementPage />} />
                  <Route path="tesoreria/banche" element={<BanksPage />} />
                  <Route path="tesoreria/anticipi" element={<AdvancesPage />} />
                  <Route path="tesoreria/giroconti" element={<TransfersPage />} />
                  <Route path="tesoreria/autofatture" element={<SelfBillingPage />} />
                  <Route path="tesoreria/scadenzario" element={<SchedulePage />} />
                  <Route path="tesoreria/f24" element={<F24Page />} />
                  <Route path="tesoreria/f24/:id" element={<F24DeliveryPage />} />
                  <Route path="tesoreria/rateazioni" element={<InstalmentPlansPage />} />
                  <Route path="tesoreria/rateazioni/:id" element={<InstalmentPlanPage />} />
                  <Route path="tesoreria/mutui" element={<LoansPage />} />
                  <Route path="tesoreria/mutui/:id" element={<LoanPage />} />
                  {user.permissions.includes('tesoreria.cards') && (
                    <Route path="tesoreria/carte" element={<CardsPage />} />
                  )}
                  <Route path="tesoreria/banche/:code" element={<BankMovementsPage />} />
                </>
              )}
              {user.permissions.includes('organizzazione.read') && (
                <>
                  <Route
                    path="organizzazione"
                    element={<Navigate to="/organizzazione/ruoli" replace />}
                  />
                  <Route path="organizzazione/ruoli" element={<RolesPage />} />
                  <Route path="organizzazione/responsabilita" element={<ResponsibilitiesPage />} />
                  <Route path="organizzazione/regole" element={<RulesPage />} />
                </>
              )}
              {user.permissions.includes('controllo.read') && (
                <>
                  <Route path="controllo" element={<Navigate to="/controllo/piani" replace />} />
                  <Route path="controllo/piani" element={<ReclassPlansPage />} />
                </>
              )}
              {user.permissions.includes('report.dashboard') && (
                <>
                  <Route path="report" element={<Navigate to="/report/dashboard" replace />} />
                  <Route path="report/dashboard" element={<DashboardPage />} />
                </>
              )}
              {user.permissions.includes('persone.read') && (
                <>
                  <Route path="organizzazione/persone" element={<EmployeesPage />} />
                  <Route path="organizzazione/persone/stipendi" element={<MonthSalariesPage />} />
                  {user.permissions.includes('persone.costs') && (
                    <Route path="organizzazione/persone/costo" element={<PayrollCostPage />} />
                  )}
                  <Route path="organizzazione/persone/:id" element={<EmployeePage />} />
                </>
              )}
              {user.isAdmin && <Route path="admin/utenti" element={<UsersPage />} />}
              {user.isAdmin && <Route path="admin/ruoli" element={<ProfilesPage />} />}
              {user.isAdmin && <Route path="admin/backup" element={<BackupsPage />} />}
              {user.isAdmin && <Route path="admin/sharepoint" element={<SharePointPage />} />}
              {user.isAdmin && <Route path="admin/gestify" element={<GestifyPage />} />}
              {user.isAdmin && <Route path="admin/registro" element={<AuditPage />} />}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </GestifyBrowserProvider>
      );
    }
  }
}
